// src/components/gatherings/NeedsMoreInfoModal.tsx
import React, { useState } from 'react';
import { Modal, Box, IconButton, Typography, TextField, Button, CircularProgress, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GatheringStatus } from '../../../../types';
import { gatheringDataService } from '../../../../services/data/gatheringService';

interface NeedsMoreInfoModalProps {
  gatheringId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  token: string;
  gatheringTitle?: string;
  status: GatheringStatus;
}

// Style for the modal content using MUI patterns
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '600px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  borderRadius: 2,
  outline: 0,
};

const NeedsMoreInfoModal: React.FC<NeedsMoreInfoModalProps> = ({
  gatheringId,
  isOpen,
  onClose,
  onSuccess,
  token,
  gatheringTitle,
  status
}) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    // Validate notes
    if (!notes.trim()) {
      setError('Please provide notes explaining what additional information is needed.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Use the admin endpoint with notes
      await gatheringDataService.updateGatheringStatusAsAdmin(
        gatheringId,
        status,
        notes
      );
      
      // Reset form and close modal
      setNotes('');
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setError('Failed to update status. Please try again.');
      console.error('Error submitting notes:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNotes('');
    setError(null);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={isSubmitting ? undefined : handleCancel}
      aria-labelledby="needs-more-info-modal-title"
    >
      <Box sx={modalStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography id="needs-more-info-modal-title" variant="h6" component="h2">
          {status === GatheringStatus.RESCHEDULE
            ? 'Request Reschedule'
            : 'Request More Information'}
        </Typography>
          <IconButton 
            aria-label="close" 
            onClick={handleCancel}
            disabled={isSubmitting}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {gatheringTitle && (
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            <strong>Gathering:</strong> {gatheringTitle}
          </Typography>
        )}

        <Typography variant="body1" sx={{ mb: 2 }}>
          {status === GatheringStatus.RESCHEDULE
            ? 'Please provide notes explaining why this gathering needs to be rescheduled. These notes will be sent to the organizer via email.'
            : 'Please provide notes explaining what additional information is needed from the gathering organizer. These notes will be sent to the organizer via email.'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Notes"
          multiline
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter details about what additional information is needed..."
          fullWidth
          error={!!error}
          disabled={isSubmitting}
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={isSubmitting || !notes.trim()}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default NeedsMoreInfoModal;
